import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Box, Container } from '@mui/material';
import { Formik } from 'formik';
import { useNavigate, useLocation } from 'react-router-dom';
import Snackbar from '@mui/material/Snackbar';
import { useDispatch, useSelector } from 'react-redux';

import { CONSTANTS, STRINGS } from 'src/commons';
import Alert from 'src/components/Alert';
import { commonReducerAction } from 'src/redux/reducers/common';
import { loaderReducerAction } from 'src/redux/reducers/loader';
import { membershipPlanReducerAction } from 'src/redux/reducers/membershipPlan';
import { getMembershipPlan, updateMembershipPlan } from 'src/api/membership';
import MembershipPlanForm from 'src/components/membership/MembershipForm';

const UpdateMembershipPlan = () => {
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: '',
    severity: ''
  });
  const { startLoading, stopLoading } = loaderReducerAction;
  const dispatch = useDispatch();
  const { user, gymId } = useSelector((state) => state.common);
  const { updateMembershipPlans } = membershipPlanReducerAction;
  const { logoutUser } = commonReducerAction;
  const { membershipPlanList } = useSelector((state) => state.membershipPlan);

  const [membershipPlan, setMembershipPlan] = useState({
    name: '',
    description: '',
    amount: null,
    duration: 0,
    _id: ''
  });

  const location = useLocation();
  const _id = location?.pathname?.split('/')?.pop();

  useEffect(() => {
    if (membershipPlanList?.length) {
      const membershipPlan = membershipPlanList?.find(
        (membershipPlan) => membershipPlan?._id === _id
      );
      if (membershipPlan) {
        setMembershipPlan(membershipPlan);
      }
    } else {
      dispatch(startLoading());
      getMembershipPlan(user, gymId)
        .then((res) => {
          if (res.status === CONSTANTS.STATUS_CODES.FORBIDDEN)
            dispatch(logoutUser());
          if (res.status === CONSTANTS.STATUS_CODES.SUCCESS) {
            dispatch(updateMembershipPlans(res?.data?.data));
          }
          dispatch(stopLoading());
        })
        .catch((err) => {
          console.log(err);
          dispatch(stopLoading());
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membershipPlanList]);

  const navigate = useNavigate();

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setSnackbarState({
      ...snackbarState,
      open: false
    });
  };

  return (
    <>
      <Helmet>
        <title>Membership | GMS</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          marginTop: '2rem'
        }}
      >
        <Container maxWidth="sm">
          <Formik
            initialValues={{
              name: membershipPlan?.name,
              description: membershipPlan?.description,
              amount: membershipPlan?.amount,
              duration: membershipPlan?.duration
            }}
            enableReinitialize
            validationSchema={CONSTANTS.membershipPlanValidationSchema}
            onSubmit={async (values) => {
              values.gym_id = gymId;
              try {
                dispatch(startLoading());
                const res = await updateMembershipPlan(
                  user,
                  values,
                  membershipPlan?._id
                );
                dispatch(stopLoading());

                const isError = res.status !== 200;
                const message = isError ? res?.message : "Membership Plan Updated Successfully"
                setSnackbarState({
                  open: true,
                  message: message,
                  severity: isError ? 'error' : 'success'
                });
                if (!isError) {
                  navigate(CONSTANTS.ROUTES.PRIVATE_ROUTES.MEMBERSHIP_PLAN);
                }
              } catch (err) {
                dispatch(stopLoading());
                setSnackbarState({
                  open: true,
                  message: err.message,
                  severity: 'error'
                });
              }
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              setFieldValue,
              isSubmitting,
              touched,
              values
            }) => (
              <MembershipPlanForm
                errors={errors}
                handleBlur={handleBlur}
                handleChange={handleChange}
                handleSubmit={handleSubmit}
                setFieldValue={setFieldValue}
                isSubmitting={isSubmitting}
                touched={touched}
                values={values}
                heading={STRINGS.UPDATE_MEMBERSHIP_PLAN}
                isFromUpdateMembershipPlan={true}
              />
            )}
          </Formik>
          <Snackbar
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            open={snackbarState.open}
            autoHideDuration={3000}
            onClose={handleClose}
          >
            <div>
              <Alert onClose={handleClose} severity={snackbarState.severity}>
                {snackbarState.message}
              </Alert>
            </div>
          </Snackbar>
        </Container>
      </Box>
    </>
  );
};

export default UpdateMembershipPlan;
